import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Mentorship,
    MentorshipReview,
    FlaggedMentorshipReviewDTO,
    UpdateMentorshipDto,
    LogSessionDto,
    CompleteMentorshipDto,
    UpsertMentorshipReviewDto,
    FlagMentorshipReviewDto,
    ProfileMentorshipSummaryDTO,
    ApiResponse,
    PaginatedResponse
} from '../../../shared/types';
import axiosService from '../services/axios.service';
import { MentorshipPopulatedProfiles } from '../../../shared/types/entities';
import { useAnyCrewModuleEnabled } from './useCrews';
import { mentorKeys } from './useMentors';
import { profileKeys } from './useProfiles';

// Query key factory
export const mentorshipKeys = {
    all: ['mentorships'] as const,
    crews: (crewSlug: string) => [...mentorshipKeys.all, 'crew', crewSlug] as const,
    myMentorships: (crewSlug: string, role?: 'mentor' | 'mentee' | 'both', status?: string) =>
        [...mentorshipKeys.crews(crewSlug), 'my-mentorships', { role, status }] as const,
    detail: (crewSlug: string, mentorshipId: string) =>
        [...mentorshipKeys.crews(crewSlug), 'detail', mentorshipId] as const,
    review: (crewSlug: string, mentorshipId: string) =>
        [...mentorshipKeys.crews(crewSlug), 'review', mentorshipId] as const,
    stats: (crewSlug: string) =>
        [...mentorshipKeys.crews(crewSlug), 'stats'] as const
};

// API functions
const mentorshipApi = {
    // Get my mentorships (as mentor or mentee)
    getMyMentorships: async (
        crewSlug: string,
        role?: 'mentor' | 'mentee' | 'both',
        status?: string
    ): Promise<PaginatedResponse<MentorshipPopulatedProfiles>> => {
        const { data } = await axiosService.get<PaginatedResponse<MentorshipPopulatedProfiles>>(
            `/c/${crewSlug}/mentorships/my-mentorships`,
            { params: { role, status } }
        );
        return data;
    },

    // Get a profile's active mentorships (crew-public: counterpart + period only)
    getProfileMentorships: async (
        crewSlug: string,
        profileId: string
    ): Promise<ApiResponse<ProfileMentorshipSummaryDTO[]>> => {
        const { data } = await axiosService.get<ApiResponse<ProfileMentorshipSummaryDTO[]>>(
            `/c/${crewSlug}/mentorships/profile/${profileId}`
        );
        return data;
    },

    // Get specific mentorship by ID
    getMentorship: async (crewSlug: string, mentorshipId: string): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.get<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}`
        );
        return data;
    },

    // Update mentorship details
    updateMentorship: async (
        crewSlug: string,
        mentorshipId: string,
        updateData: UpdateMentorshipDto
    ): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.put<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}`,
            updateData
        );
        return data;
    },

    // Log a session
    logSession: async (
        crewSlug: string,
        mentorshipId: string,
        sessionData: LogSessionDto
    ): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.post<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/session`,
            sessionData
        );
        return data;
    },

    // Complete mentorship
    completeMentorship: async (
        crewSlug: string,
        mentorshipId: string,
        completionData: CompleteMentorshipDto
    ): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.post<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/complete`,
            completionData
        );
        return data;
    },

    // Pause mentorship
    pauseMentorship: async (crewSlug: string, mentorshipId: string): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.post<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/pause`
        );
        return data;
    },

    // Resume mentorship
    resumeMentorship: async (crewSlug: string, mentorshipId: string): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.post<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/resume`
        );
        return data;
    },

    // Cancel mentorship
    cancelMentorship: async (
        crewSlug: string,
        mentorshipId: string,
        reason?: string
    ): Promise<ApiResponse<Mentorship>> => {
        const { data } = await axiosService.delete<ApiResponse<Mentorship>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/cancel`,
            { data: { reason } }
        );
        return data;
    },

    // Get crew-wide mentorship stats (admin only)
    getStats: async (crewSlug: string): Promise<ApiResponse<any>> => {
        const { data } = await axiosService.get<ApiResponse<any>>(
            `/c/${crewSlug}/mentorships/stats`
        );
        return data;
    },

    // Get review for a mentorship
    getReview: async (crewSlug: string, mentorshipId: string): Promise<ApiResponse<MentorshipReview | null>> => {
        const { data } = await axiosService.get<ApiResponse<MentorshipReview | null>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/review`
        );
        return data;
    },

    // Upsert review (mentee only)
    upsertReview: async (
        crewSlug: string,
        mentorshipId: string,
        dto: UpsertMentorshipReviewDto
    ): Promise<ApiResponse<MentorshipReview>> => {
        const { data } = await axiosService.put<ApiResponse<MentorshipReview>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/review`,
            dto
        );
        return data;
    },

    // Admin: Delete review
    deleteReview: async (crewSlug: string, mentorshipId: string): Promise<ApiResponse<void>> => {
        const { data } = await axiosService.delete<ApiResponse<void>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/review`
        );
        return data;
    },

    // Mentor: Flag review
    flagReview: async (crewSlug: string, mentorshipId: string, dto: FlagMentorshipReviewDto): Promise<ApiResponse<MentorshipReview>> => {
        const { data } = await axiosService.post<ApiResponse<MentorshipReview>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/review/flag`,
            dto
        );
        return data;
    },

    // Admin: Unflag (dismiss) review
    unflagReview: async (crewSlug: string, mentorshipId: string): Promise<ApiResponse<MentorshipReview>> => {
        const { data } = await axiosService.delete<ApiResponse<MentorshipReview>>(
            `/c/${crewSlug}/mentorships/${mentorshipId}/review/flag`
        );
        return data;
    },

    // Admin: Get all flagged reviews
    getFlaggedReviews: async (crewSlug: string): Promise<ApiResponse<FlaggedMentorshipReviewDTO[]>> => {
        const { data } = await axiosService.get<ApiResponse<FlaggedMentorshipReviewDTO[]>>(
            `/c/${crewSlug}/mentorships/flagged-reviews`
        );
        return data;
    }
};

// React Query hooks

// Get my mentorships
export const useMyMentorships = (
    crewSlug: string,
    role?: 'mentor' | 'mentee' | 'both',
    status?: string
) => {
    const mentorshipEnabled = useAnyCrewModuleEnabled(crewSlug, ['mentorship', 'mentorshipPlans']);
    return useQuery({
        queryKey: mentorshipKeys.myMentorships(crewSlug, role, status),
        queryFn: () => mentorshipApi.getMyMentorships(crewSlug, role, status),
        enabled: !!crewSlug && mentorshipEnabled
    });
};

// Get a profile's active mentorships (crew-public view)
export const useProfileMentorships = (crewSlug: string, profileId: string | undefined) => {
    const mentorshipEnabled = useAnyCrewModuleEnabled(crewSlug, ['mentorship', 'mentorshipPlans']);
    return useQuery({
        queryKey: [...mentorshipKeys.crews(crewSlug), 'profile-mentorships', profileId],
        queryFn: () => mentorshipApi.getProfileMentorships(crewSlug, profileId!),
        enabled: !!crewSlug && !!profileId && mentorshipEnabled,
        select: (res) => res.data
    });
};

// Get specific mentorship
export const useMentorship = (crewSlug: string, mentorshipId: string) => {
    return useQuery({
        queryKey: mentorshipKeys.detail(crewSlug, mentorshipId),
        queryFn: () => mentorshipApi.getMentorship(crewSlug, mentorshipId),
        enabled: !!crewSlug && !!mentorshipId
    });
};

// Get mentorship stats
export const useMentorshipStats = (crewSlug: string) => {
    const mentorshipEnabled = useAnyCrewModuleEnabled(crewSlug, ['mentorship', 'mentorshipPlans']);
    return useQuery({
        queryKey: mentorshipKeys.stats(crewSlug),
        queryFn: () => mentorshipApi.getStats(crewSlug),
        enabled: !!crewSlug && mentorshipEnabled
    });
};

// Update mentorship
export const useUpdateMentorship = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorshipId, data }: { mentorshipId: string; data: UpdateMentorshipDto }) =>
            mentorshipApi.updateMentorship(crewSlug, mentorshipId, data),
        onSuccess: (_, { mentorshipId }) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.detail(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
        }
    });
};

// Log session
export const useLogSession = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorshipId, data }: { mentorshipId: string; data: LogSessionDto }) =>
            mentorshipApi.logSession(crewSlug, mentorshipId, data),
        onSuccess: (_, { mentorshipId }) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.detail(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
        }
    });
};

// Complete mentorship
export const useCompleteMentorship = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorshipId, data }: { mentorshipId: string; data: CompleteMentorshipDto }) =>
            mentorshipApi.completeMentorship(crewSlug, mentorshipId, data),
        onSuccess: (_, { mentorshipId }) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.detail(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.stats(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
            // Completing frees up a slot on the mentor's Profile.currentMentees.
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

// Pause mentorship
export const usePauseMentorship = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (mentorshipId: string) => mentorshipApi.pauseMentorship(crewSlug, mentorshipId),
        onSuccess: (_, mentorshipId) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.detail(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
        }
    });
};

// Resume mentorship
export const useResumeMentorship = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (mentorshipId: string) => mentorshipApi.resumeMentorship(crewSlug, mentorshipId),
        onSuccess: (_, mentorshipId) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.detail(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
        }
    });
};

// Cancel mentorship
export const useCancelMentorship = (crewSlug: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ mentorshipId, reason }: { mentorshipId: string; reason?: string }) =>
            mentorshipApi.cancelMentorship(crewSlug, mentorshipId, reason),
        onSuccess: (_, { mentorshipId }) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.detail(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.crews(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.stats(crewSlug) });
            queryClient.invalidateQueries({ queryKey: mentorKeys.crews(crewSlug) });
            // Cancelling frees up a slot on the mentor's Profile.currentMentees.
            queryClient.invalidateQueries({ queryKey: profileKeys.crews(crewSlug) });
        }
    });
};

// Get review for a mentorship
export const useMentorshipReview = (crewSlug: string, mentorshipId: string | undefined) => {
    return useQuery({
        queryKey: mentorshipKeys.review(crewSlug, mentorshipId ?? ''),
        queryFn: () => mentorshipApi.getReview(crewSlug, mentorshipId!),
        enabled: !!crewSlug && !!mentorshipId,
        select: (res) => res.data
    });
};

// Upsert review (save draft or update)
export const useUpsertMentorshipReview = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ mentorshipId, dto }: { mentorshipId: string; dto: UpsertMentorshipReviewDto }) =>
            mentorshipApi.upsertReview(crewSlug, mentorshipId, dto),
        onSuccess: (_, { mentorshipId }) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.review(crewSlug, mentorshipId) });
        }
    });
};

// Admin: Delete a review
export const useDeleteMentorshipReview = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (mentorshipId: string) => mentorshipApi.deleteReview(crewSlug, mentorshipId),
        onSuccess: (_result, mentorshipId) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.review(crewSlug, mentorshipId) });
        }
    });
};

// Mentor: Flag a review
export const useFlagMentorshipReview = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ mentorshipId, dto }: { mentorshipId: string; dto: FlagMentorshipReviewDto }) =>
            mentorshipApi.flagReview(crewSlug, mentorshipId, dto),
        onSuccess: (_, { mentorshipId }) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.review(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: [...mentorshipKeys.crews(crewSlug), 'flagged-reviews'] });
        }
    });
};

// Admin: Dismiss (unflag) a review
export const useUnflagMentorshipReview = (crewSlug: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (mentorshipId: string) => mentorshipApi.unflagReview(crewSlug, mentorshipId),
        onSuccess: (_, mentorshipId) => {
            queryClient.invalidateQueries({ queryKey: mentorshipKeys.review(crewSlug, mentorshipId) });
            queryClient.invalidateQueries({ queryKey: [...mentorshipKeys.crews(crewSlug), 'flagged-reviews'] });
        }
    });
};

// Admin: Get all flagged reviews
export const useFlaggedMentorshipReviews = (crewSlug: string | undefined, enabled = false) => {
    return useQuery({
        queryKey: [...mentorshipKeys.crews(crewSlug ?? ''), 'flagged-reviews'],
        queryFn: () => mentorshipApi.getFlaggedReviews(crewSlug!),
        enabled: !!crewSlug && enabled,
        select: (res) => res.data
    });
};
